import { esc } from './customer-export'
import { dayIn } from './round-config'
import type { Invoice, InvoiceState } from './invoices'

// More > Invoices' records (Liam, 22 Oct 2026: "a list of past invoices generated, useful for taxes or looking back";
// docs/plans/invoice-maker/PLAN.md 4, Astra r2, Sonnet's money review). Pure, so the checks run it without a page.

// An invoice as the list and the file read it: the copy it keeps, plus the customer's name now and the numbers it is
// linked to (GET /api/invoices?all=1).
export type RecordRow = Invoice & { customer_name?: string | null; credit_for_number?: string | null }

// Where it stands, for the list and the file. Not sent yet comes before Paid (Sonnet: one made from paid visits and never
// sent read as Paid); cancelled and credit notes say so whatever their money.
export function recordState(r: Pick<Invoice, 'kind' | 'cancelled_at' | 'sent_at' | 'state'>): InvoiceState {
  if (r.kind === 'credit') return 'credit'
  if (r.cancelled_at) return 'cancelled'
  if (!r.sent_at) return 'not_sent'
  return r.state
}

// The tax years a round counts in: a UK round 6 April to 5 April, a US or Canadian round the calendar year. Both ends
// are counted. `today` is the round's own day (roundDay), never the phone's clock.
export interface DateRange { from: string; to: string; label: string }
export function taxYears(today: string, country: string): { thisYear: DateRange; lastYear: DateRange } {
  const y = Number(today.slice(0, 4))
  if (country !== 'GB') return {
    thisYear: { from: `${y}-01-01`, to: `${y}-12-31`, label: `${y}` },
    lastYear: { from: `${y - 1}-01-01`, to: `${y - 1}-12-31`, label: `${y - 1}` },
  }
  const start = today.slice(5) >= '04-06' ? y : y - 1
  const year = (s: number): DateRange => ({ from: `${s}-04-06`, to: `${s + 1}-04-05`, label: `6 April ${s} to 5 April ${s + 1}` })
  return { thisYear: year(start), lastYear: year(start - 1) }
}

// A server time ("2026-10-22 23:30:00", UTC) as the round's day, so a send at half past midnight in summer is the day
// it happened in the UK (Sonnet: sent and cancelled dates were UTC beside a London issue date).
export function roundDate(stamp: string | null | undefined, zone: string): string {
  if (!stamp) return ''
  const d = new Date(`${stamp.replace(' ', 'T')}${/Z|[+-]\d\d:?\d\d$/.test(stamp) ? '' : 'Z'}`)
  return Number.isNaN(d.getTime()) ? stamp.slice(0, 10) : dayIn(zone, d)
}

const STATE_FILE: Record<InvoiceState, string> = {
  paid: 'Paid', part_paid: 'Part paid', sent: 'Sent, not paid', not_sent: 'Not sent yet', late: 'Late', cancelled: 'Cancelled', credit: 'Credit note',
}
const KIND_FILE: Record<Invoice['kind'], string> = { visit: 'Invoice', month: 'Monthly invoice', credit: 'Credit note' }
const VIA_FILE: Record<string, string> = { email: 'Email from Roundly', whatsapp: 'WhatsApp', sms: 'Text', link: 'By you (PDF or link)' }
// Money as a plain number a spreadsheet adds up: never through esc, which would make "-40.00" text (Sonnet, Astra).
const amount = (pence: number | null | undefined): string => (pence === null || pence === undefined ? '' : (pence / 100).toFixed(2))

// One line for each document made in the range, in number order. Gross is the full price of its lines; "already paid or
// credit" is what was taken off when it was made; the amount asked for is what it asked for then. A credit note's
// figures are negative. "Counts" is No only for an invoice cancelled with no credit note: one cancelled by a credit note
// counts with it, the two coming to nothing together.
export function invoiceRecordsCsv(rows: RecordRow[], opts: { zone: string; currency: string; vat: boolean; today: string }): string {
  const sorted = [...rows].sort((a, b) => ((a as { seq?: number }).seq ?? 0) - ((b as { seq?: number }).seq ?? 0) || a.id - b.id)
  const creditedNumbers = new Set(rows.filter((r) => r.kind === 'credit' && r.credit_for_number).map((r) => r.credit_for_number!))
  const head = ['Number', 'Issued', 'Type', 'For month', 'Credit note for', 'Credited by', 'Cancelled on', 'Customer', 'Invoiced to', 'Lines', 'Gross',
    ...(opts.vat ? ['VAT rate %', 'VAT', 'Net of VAT'] : []), 'Already paid or credit', 'Amount asked for when made', 'Currency', 'Due', 'Sent on', 'Sent by',
    ...(opts.vat ? ['Your VAT number'] : []), `State on ${opts.today}`, 'Counts']
  const out = [esc('Invoice records, including unsent and cancelled documents. Not money received.'), head.map(esc).join(',')]
  for (const r of sorted) {
    const credit = r.kind === 'credit'
    const gross = credit ? r.total_pence : r.subtotal_pence
    const vat = r.vat_pence ?? null
    const cancelledNoCredit = !!r.cancelled_at && !r.credited_by && !creditedNumbers.has(r.number)
    const text = [
      r.number, r.issued_on, KIND_FILE[r.kind] ?? r.kind, r.kind === 'month' && r.period ? r.period : '', r.credit_for_number ?? '', r.credited_by ?? '',
      roundDate(r.cancelled_at, opts.zone), r.to?.for_customer || r.to?.name || r.customer_name || '', r.to?.name ?? '',
      (r.lines ?? []).map((l) => l.label).join('; '),
    ].map((v) => esc(String(v)))
    const money = [amount(gross), ...(opts.vat ? [r.vat_rate_bp == null ? '' : String(r.vat_rate_bp / 100), amount(vat), vat == null ? '' : amount(gross - vat)] : []),
      amount(credit ? 0 : r.less_pence), amount(r.total_pence)]
    const after = [opts.currency, r.due_on ?? '', roundDate(r.sent_at, opts.zone), r.sent_via ? VIA_FILE[r.sent_via] ?? r.sent_via : '',
      ...(opts.vat ? [r.from?.vat_number ?? ''] : []), STATE_FILE[recordState(r)], cancelledNoCredit ? 'No' : 'Yes'].map((v) => esc(String(v)))
    out.push([...text, ...money, ...after].join(','))
  }
  // A byte-order mark so Excel reads £ and accents as they are (customer-export.ts does the same).
  return '﻿' + out.join('\r\n') + '\r\n'
}
